import { memo, useCallback, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { useErrorToast } from "~/hooks/use-error-toast";
import { SearchQuery, SearchType } from "~/hooks/search/types";

import { buildCatalogPayload } from "~/pages/books/analytics/build-catalog-payload";
import { diffFilters } from "~/pages/books/analytics/diff-filters";
import { useCatalogAnalytics } from "~/pages/books/analytics/use-catalog-analytics";
import { SeasonalEventModal } from "~/components/seasonal-events";
import { useSeasonalEventsQuery } from "~/components/seasonal-events/hooks/use-seasonal-events-query";
import { SearchTypeDropdown } from "./input/search-type-dropdown";
import { SeasonalEventsSearchArea } from "./seasonal-events/seasonal-events-search-area";
import { useSeasonalEventModal } from "./seasonal-events/use-seasonal-event-modal";
import { SearchFilters } from "./filters/search-filters";
import { BooksSearchInput } from "./input/books-search-input";
import { defaultFilterValues } from "./filters/constants";
import { getNoOfValidFilters } from "./filters/utils";

interface SearchToolbarProps {
  searchQuery: SearchQuery;
  setSearchQuery: (query: Partial<SearchQuery>) => void;
  error: Error | null | undefined;
  clearSelection(): void;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
}

export const SearchToolbar = memo(
  ({
    searchQuery,
    setSearchQuery,
    error,
    clearSelection,
    ageRangeAvailableBounds,
  }: SearchToolbarProps) => {
    const committedQueryRef = useRef(searchQuery.query);
    const isSyncingFromPropsRef = useRef(false);
    const prevQueryRef = useRef(searchQuery.query);
    const form = useForm<SearchQuery>({
      defaultValues: {
        ...searchQuery,
        filters: {
          ...defaultFilterValues,
          ...searchQuery.filters,
        },
      },
    });

    const { control, watch, handleSubmit, getValues, reset } = form;
    const { emit: catalogEmit } = useCatalogAnalytics();

    // External searchQuery updates (e.g. seasonal prompts) must sync the form;
    // defaultValues only apply on mount.
    useEffect(() => {
      const queryChanged = prevQueryRef.current !== searchQuery.query;
      prevQueryRef.current = searchQuery.query;
      isSyncingFromPropsRef.current = true;
      reset(
        {
          ...searchQuery,
          filters: {
            ...defaultFilterValues,
            ...searchQuery.filters,
          },
        },
        // Non-query syncs (pagination, filter apply) must not wipe an uncommitted draft.
        queryChanged ? undefined : { keepDirtyValues: true },
      );
      committedQueryRef.current = searchQuery.query;
      isSyncingFromPropsRef.current = false;
    }, [searchQuery, reset]);

    const onSearchQueryChange = useCallback(
      (value: Partial<SearchQuery>) => {
        setSearchQuery({ ...value, page: null });
        clearSelection();
      },
      [setSearchQuery, clearSelection],
    );

    const commitQuery = useCallback(
      (newQuery: string, formValues: SearchQuery) => {
        const previousQuery = committedQueryRef.current ?? "";
        if (newQuery !== previousQuery) {
          catalogEmit.queryCommitted(
            previousQuery,
            buildCatalogPayload({ ...formValues, query: newQuery, page: null }),
          );
        }
        committedQueryRef.current = newQuery;
      },
      [catalogEmit],
    );

    const onSubmit = useCallback(
      (value: SearchQuery) => {
        commitQuery(value.query, value);
        onSearchQueryChange(value);
      },
      [commitQuery, onSearchQueryChange],
    );

    const onClear = useCallback(() => {
      // Clear skips catalog_query_committed; zero committedQueryRef so payloads match the cleared input.
      committedQueryRef.current = "";
      // Type survives so an empty semantic input still offers the seasonal
      // pills. page is nulled by onSearchQueryChange anyway; setting it here
      // keeps the emitted payload and the committed query built from one object.
      const clearedQuery = {
        ...getValues(),
        query: "",
        page: null,
        pageSize: null,
      };
      catalogEmit.searchCleared(buildCatalogPayload(clearedQuery));
      onSearchQueryChange(clearedQuery);
    }, [catalogEmit, getValues, onSearchQueryChange]);

    useEffect(() => {
      const subscription = watch((_, { name }) => {
        // Skip notifications from the props→form sync reset above.
        // SearchTypeDropdown also uses reset(); those must still propagate.
        if (isSyncingFromPropsRef.current) {
          return;
        }

        const currentValues = getValues();

        if (name?.startsWith("filters.")) {
          return;
        }

        if (name === "query" && currentValues.type !== SearchType.ISBN) {
          return;
        }

        const query =
          name === "query"
            ? (currentValues.query ?? "")
            : (committedQueryRef.current ?? "");

        if (name === "query") {
          commitQuery(query, currentValues);
        }

        onSearchQueryChange({
          ...currentValues,
          query,
        });
      });

      return () => {
        subscription.unsubscribe();
      };
    }, [watch, getValues, onSearchQueryChange, commitQuery]);

    const onApplyFilters = useCallback(() => {
      // A stale applied age range is already reset inside the drawer when the
      // first other filter changes, so the draft applies as-is.
      const current = getValues();

      const nextQuery: SearchQuery = {
        ...current,
        query: committedQueryRef.current,
        page: null,
      };

      const diff = diffFilters(searchQuery.filters ?? {}, current.filters);
      catalogEmit.filterCommit(diff, buildCatalogPayload(nextQuery));

      onSearchQueryChange(nextQuery);
    }, [getValues, onSearchQueryChange, searchQuery.filters, catalogEmit]);

    const onSheetOpen = useCallback(() => {
      catalogEmit.filterSheetOpened(buildCatalogPayload(searchQuery));
    }, [catalogEmit, searchQuery]);

    const onTypeChange = useCallback(
      (fromType: SearchType, toType: SearchType) => {
        // ISBN transition clears query in SearchTypeDropdown; use committedQueryRef, not the uncommitted draft.
        const isIsbnTransition =
          toType === SearchType.ISBN || fromType === SearchType.ISBN;
        const committedQuery = isIsbnTransition
          ? ""
          : (committedQueryRef.current ?? "");
        const nextQuery: SearchQuery = {
          ...getValues(),
          type: toType,
          page: null,
          query: committedQuery,
          ...(isIsbnTransition ? { filters: { ...defaultFilterValues } } : {}),
        };
        if (isIsbnTransition) {
          committedQueryRef.current = "";
        }
        catalogEmit.searchTypeChanged(fromType, buildCatalogPayload(nextQuery));
      },
      [catalogEmit, getValues],
    );

    const {
      renderedEvent,
      isOpen: isSeasonalEventModalOpen,
      openSeasonalEventModal,
      onOpenChange: onSeasonalEventModalOpenChange,
      onRunSearch: onSeasonalEventRunSearch,
    } = useSeasonalEventModal({ searchQuery, setSearchQuery, clearSelection });

    // Same React Query key as the pills dropdown, so the calendar reuses that
    // fetch rather than issuing its own. Raw list on purpose: the calendar does
    // its own past/elapsed filtering, unlike the pills' in-window subset.
    const { events: seasonalEvents } = useSeasonalEventsQuery();

    const activeFilters = getNoOfValidFilters(searchQuery.filters ?? {});

    useErrorToast(error);

    return (
      <FormProvider {...form}>
        <div className="flex space-x-3 items-center">
          <form
            className="flex flex-row w-full relative min-h-10 max-h-24 space-x-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="relative flex flex-row w-full">
              <SearchTypeDropdown
                control={control}
                onTypeChange={onTypeChange}
              />
              <SeasonalEventsSearchArea onEventSelect={openSeasonalEventModal}>
                <BooksSearchInput name="query" onClear={onClear} />
              </SeasonalEventsSearchArea>
            </div>
            <SearchFilters
              activeFilters={activeFilters}
              onApply={onApplyFilters}
              onSheetOpen={onSheetOpen}
              appliedFilters={searchQuery.filters ?? {}}
              searchQuery={searchQuery.query}
              searchType={searchQuery.type}
              searchPageSize={searchQuery.pageSize}
              ageRangeAvailableBounds={ageRangeAvailableBounds}
            />
          </form>
        </div>
        {renderedEvent ? (
          <SeasonalEventModal
            initialEvent={renderedEvent}
            events={seasonalEvents}
            surface="books"
            open={isSeasonalEventModalOpen}
            onOpenChange={onSeasonalEventModalOpenChange}
            onRunSearch={onSeasonalEventRunSearch}
          />
        ) : null}
      </FormProvider>
    );
  },
);

SearchToolbar.displayName = "SearchToolbar";
